/**
 * Which tool a gesture belongs to.
 *
 * One pointer pipeline serves the whole board (docs/03-ARCHITECTURE.md §7.2), so
 * something has to say whether the hand going down means "write here" or "rub this
 * out". That decision is made once, when the hand goes down, and held until it lifts:
 * a tool picked up mid-stroke must not turn half a line into half an erase.
 */

import type { Point, ToolId } from '@opal/shared';

import type { EraseSession } from './erase-session.js';
import type { InkSample, InkSession } from './ink-session.js';
import type { LaserSession } from './laser-session.js';
import type { SelectSession } from './select-session.js';
import type { ShapeSession } from './shape-session.js';
import type { TextSession } from './text-session.js';
import type { ViewportState } from '@opal/contracts';

/** The handlers the pointer pipeline calls. */
export interface InputHandlers {
  readonly onBegin: (sample: InkSample) => void;
  readonly onExtend: (samples: readonly InkSample[]) => void;
  readonly onFinish: () => void;
}

export interface InputTargets {
  readonly ink: InkSession;
  readonly erase: EraseSession;
  readonly select: SelectSession;
  /** SRS C4 - the shapes tool, which draws on the same layer the pen does. */
  readonly shape: ShapeSession;
  /** SRS C4 - the text tool and the sticky notes. One tap, no drag. */
  readonly text: TextSession;
  /** SRS C4 - the laser pointer. Leaves a trail that fades, and nothing else. */
  readonly laser: LaserSession;
  /** What is in the hand. Read at the moment it goes down. */
  readonly tool: () => ToolId;
  /** Where the board is looking. Read at the moment the hand goes down. */
  readonly viewport: () => ViewportState;
}

/** The canvas point a sample landed on. */
function pointOf(sample: InkSample): Point {
  return { x: sample.x, y: sample.y };
}

/** Sends pointer input to whichever tool the hand went down with. */
export function createInputHandlers(targets: InputTargets): InputHandlers {
  let holding: ToolId = 'pen';

  return {
    onBegin: (sample) => {
      holding = targets.tool();
      if (holding === 'eraser') targets.erase.begin(pointOf(sample));
      else if (holding === 'select') targets.select.begin(pointOf(sample));
      else if (holding === 'shape') targets.shape.begin(pointOf(sample));
      // A text box and a sticky note are placed by a tap: there is nothing to drag,
      // so the whole gesture happens the moment the hand goes down.
      else if (holding === 'text' || holding === 'sticky') targets.text.place(pointOf(sample));
      else if (holding === 'laser') targets.laser.begin(pointOf(sample));
      else targets.ink.begin(sample, targets.viewport());
    },

    onExtend: (samples) => {
      if (holding === 'eraser') targets.erase.extend(samples.map(pointOf));
      else if (holding === 'select') targets.select.extend(samples.map(pointOf));
      else if (holding === 'shape') targets.shape.extend(samples.map(pointOf));
      else if (holding === 'text' || holding === 'sticky') return;
      else if (holding === 'laser') targets.laser.extend(samples.map(pointOf));
      else targets.ink.extend(samples);
    },

    onFinish: () => {
      if (holding === 'eraser') targets.erase.finish();
      else if (holding === 'select') targets.select.finish();
      else if (holding === 'shape') targets.shape.finish();
      else if (holding === 'laser') targets.laser.finish();
      else if (holding !== 'text' && holding !== 'sticky') targets.ink.finish();
      holding = 'pen';
    },
  };
}
